import { CheckCircle, ArrowLeft, Linkedin, Mail } from 'lucide-react'

interface ThankYouProps {
  onNavigate: (page: string) => void
}

export const ThankYou: React.FC<ThankYouProps> = ({ onNavigate }) => {
  return (
    <section
      className="min-h-screen flex items-center justify-center py-20"
      aria-labelledby="thank-you-heading"
    >
      <div className="container-narrow text-center">
        {/* Success Icon */}
        <div className="mb-8 animate-fade-in">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg shadow-green-500/30">
            <CheckCircle size={48} className="text-white" aria-hidden="true" />
          </div>
        </div>

        {/* Heading */}
        <h1
          id="thank-you-heading"
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 animate-fade-in-up"
        >
          Thank You!
        </h1>

        {/* Message */}
        <div
          className="animate-fade-in-up"
          style={{ animationDelay: '0.2s' }}
        >
          <p className="text-xl text-slate-600 mb-4">
            Your message has been received.
          </p>
          <p className="text-lg text-slate-500 max-w-lg mx-auto mb-8">
            I'll review your inquiry and get back to you within 24-48 hours.
            Looking forward to discussing how we can transform your learning experience.
          </p>
        </div>

        {/* What's Next Box */}
        <div
          className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-8 mb-10 max-w-md mx-auto animate-fade-in-up"
          style={{ animationDelay: '0.4s' }}
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            What happens next?
          </h2>
          <ol className="text-left space-y-3 text-slate-600">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-blue text-white text-sm font-bold flex items-center justify-center">
                1
              </span>
              <span>I'll review your message and project details</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-blue text-white text-sm font-bold flex items-center justify-center">
                2
              </span>
              <span>You'll receive a personalized response via email</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-blue text-white text-sm font-bold flex items-center justify-center">
                3
              </span>
              <span>We'll schedule a discovery call to discuss your needs</span>
            </li>
          </ol>
        </div>

        {/* Actions */}
        <div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up"
          style={{ animationDelay: '0.6s' }}
        >
          <button
            onClick={() => onNavigate('home')}
            className="btn-secondary"
          >
            <ArrowLeft size={18} aria-hidden="true" />
            Back to Home
          </button>
          <a
            href="https://www.linkedin.com/in/giovanniduarte"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            <Linkedin size={18} aria-hidden="true" />
            Connect on LinkedIn
          </a>
        </div>

        {/* Direct Contact */}
        <p
          className="mt-10 text-slate-500 text-sm animate-fade-in-up"
          style={{ animationDelay: '0.8s' }}
        >
          Need immediate assistance?{' '}
          <a
            href="mailto:gio@learningxperience.tech"
            className="text-brand-blue hover:underline inline-flex items-center gap-1"
          >
            <Mail size={14} aria-hidden="true" />
            Email me directly
          </a>
        </p>
      </div>
    </section>
  )
}
